import { useEffect } from 'react';
import { ACCORDION_MOUNT_EVENT, SECTION_DEEP_LINK_EVENT } from '../../constants';
import { getAccordionDeepLink } from '../../productDetailsUtils';
import { useAnchorScroll } from './useAnchorScroll';

/**
 * Expands and scrolls to the collapsible section a URL query param deep links to.
 *
 * Product details sits at the top of the page and the section it has to open lives much
 * further down, in a different bundle that hydrates on its own schedule, so the two only
 * ever meet on the life cycle event bus:
 *
 * - the layout accordion publishes `accordion.mount` with its wrapper ids and an expand
 *   handle, which is how this hook opens a section it does not render;
 * - this hook republishes the parsed link as `product-details.deep-link`, so a section
 *   that owns its own open state can react to it.
 *
 * Both events go through `LIFE_CYCLE_EVENT_BUS.lifeCycle`, whose ReplaySubject hands the
 * payload to subscribers that arrive late.
 *
 * @param {object} options
 * @param {Function} options.getOffset - pixels to leave above the section while scrolling.
 */
export const useAccordionDeepLink = ({ getOffset }) => {
  const anchorToSection = useAnchorScroll(getOffset);

  useEffect(() => {
    const bus = typeof window === 'undefined' ? null : window.LIFE_CYCLE_EVENT_BUS;
    if (!bus?.lifeCycle) return undefined;

    const deepLink = getAccordionDeepLink(window.location.search);
    if (!deepLink) return undefined;

    bus.lifeCycle.trigger(SECTION_DEEP_LINK_EVENT, deepLink);

    const onAccordionMount = ({ output }) => {
      const { wrapperIds = [], setExpanded } = output || {};
      const index = wrapperIds.indexOf(deepLink.sectionId);

      // The accordion does not render this section, so there is nothing to expand and no
      // element to scroll to. Expanding by any other index would open the wrong one.
      if (index === -1 || typeof setExpanded !== 'function') return;

      // Unsubscribing is the one-shot: the accordion re-announces itself after hydration,
      // and re-scrolling would yank a reader who has since moved on.
      bus.lifeCycle.off(ACCORDION_MOUNT_EVENT, onAccordionMount);

      setExpanded(index)(undefined, true);
      // Resolve the node every frame rather than capturing it: the section renders as a
      // skeleton first and the hydrated markup replaces the element behind this id.
      anchorToSection(() => document.getElementById(deepLink.sectionId));
    };

    bus.lifeCycle.on(ACCORDION_MOUNT_EVENT, onAccordionMount);
    return () => bus.lifeCycle.off(ACCORDION_MOUNT_EVENT, onAccordionMount);
  }, [anchorToSection]);
};
